// @ts-nocheck -- consistent with the rest of src/app during the TS migration.
//
// Runtime for the Account > Settings display preferences.
//
// Theme is applied by stamping `data-theme` on <html> (not <body>): the root
// element is styled before body paints, so switching cannot flash the old
// background. `color-scheme` is set alongside it so form controls, scrollbars,
// and the browser's own UI follow the choice rather than staying dark.
//
// "system" is a live subscription, not a one-shot read — a user who flips their
// OS to light while the tab is open should see it follow.
import {
  loadThemePreference,
  saveThemePreference,
  resolveTheme,
  loadAircraftOrder,
  saveAircraftOrder,
  loadFlightOrder,
  saveFlightOrder,
  normalizeThemePreference,
  normalizeAircraftOrder,
  normalizeFlightOrder,
  loadAircraftArtView,
  saveAircraftArtView,
  normalizeAircraftArtView,
} from "../core/preferences.js";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function prefersDark() {
  const query = globalThis.matchMedia?.(DARK_QUERY);
  // No matchMedia (old browser, test stub): assume dark, which is what the site
  // has always been. Never assume light — that would flip an existing user.
  return query ? query.matches : true;
}

/** Stamp the resolved theme on <html>. Safe to call repeatedly. */
export function applyTheme(preference = loadThemePreference()) {
  const root = globalThis.document?.documentElement;
  if (!root?.setAttribute) return "dark";
  const resolved = resolveTheme(preference, prefersDark());
  root.setAttribute("data-theme", resolved);
  // Drives native control/scrollbar rendering. The stylesheet's own
  // `color-scheme` declaration covers the default; this keeps the live toggle
  // in step without waiting for a reload.
  if (root.style) root.style.colorScheme = resolved;
  return resolved;
}

let systemThemeSubscribed = false;

/**
 * Follow the OS while the preference is "system". Registered once; the handler
 * re-reads the stored preference each time so it goes quiet by itself once the
 * user picks an explicit theme.
 */
export function watchSystemTheme() {
  if (systemThemeSubscribed) return;
  const query = globalThis.matchMedia?.(DARK_QUERY);
  if (!query?.addEventListener) return;
  systemThemeSubscribed = true;
  query.addEventListener("change", () => {
    if (loadThemePreference() === "system") applyTheme("system");
  });
}

/**
 * Apply the stored theme as early as possible. Called from the app entry before
 * first paint so a light-mode user never sees a dark flash.
 */
export function initTheme() {
  const applied = applyTheme();
  watchSystemTheme();
  return applied;
}

// --- Settings panel wiring --------------------------------------------------

const CONTROLS = [
  {
    id: "accountThemePreference",
    load: loadThemePreference,
    save: saveThemePreference,
    normalize: normalizeThemePreference,
    // CSS reacts to data-theme on its own, so this needs no re-render.
    repaint: false,
    onChange: (value) => applyTheme(value),
  },
  {
    id: "accountAircraftOrder",
    load: loadAircraftOrder,
    save: saveAircraftOrder,
    normalize: normalizeAircraftOrder,
  },
  {
    id: "accountFlightOrder",
    load: loadFlightOrder,
    save: saveFlightOrder,
    normalize: normalizeFlightOrder,
  },
  {
    // Which generated default-art view rests in aircraft image slots.
    // Repaints so the workspace frame, fleet cards, and sidebar swap live.
    id: "accountAircraftArtView",
    load: loadAircraftArtView,
    save: saveAircraftArtView,
    normalize: normalizeAircraftArtView,
  },
];

/** Default flights-table sort implied by the preference. */
export function flightSortFromPreference(order = loadFlightOrder()) {
  return { key: "date", direction: normalizeFlightOrder(order) === "oldest" ? "asc" : "desc" };
}

/**
 * Order a fleet list per the preference.
 *
 * Entries are NOT always objects: the fleet sidebar's fleet list is an array of
 * tail-number STRINGS, and reading `.tail_number` off a string silently yields
 * nothing for every entry, which made the whole sort a no-op. Callers whose
 * entries are not self-describing pass accessors; the defaults below cover
 * object-shaped rows.
 */
export function orderFleetForDisplay(aircraft = [], order = loadAircraftOrder(), accessors = {}) {
  const normalized = normalizeAircraftOrder(order);
  const list = [...aircraft];
  const readText = (entry, ...keys) => {
    for (const key of keys) {
      const value = entry?.[key];
      if (value !== undefined && value !== null && value !== "") return String(value);
    }
    return "";
  };
  const readTime = (entry, ...keys) => {
    for (const key of keys) {
      const ms = Date.parse(String(entry?.[key] ?? ""));
      if (Number.isFinite(ms)) return ms;
    }
    return null;
  };
  const asMs = (value) => {
    if (value === null || value === undefined) return null;
    const ms = typeof value === "number" ? value : Date.parse(String(value));
    return Number.isFinite(ms) ? ms : null;
  };
  const tailOf = accessors.tailOf
    ? (entry) => String(accessors.tailOf(entry) ?? "")
    : (entry) => (typeof entry === "string" ? entry : readText(entry, "tail_number", "tailNumber", "aircraft"));
  const createdOf = accessors.createdAtOf
    ? (entry) => asMs(accessors.createdAtOf(entry))
    : (entry) => readTime(entry, "created_at", "createdAt");
  const activityOf = accessors.activityAtOf
    ? (entry) => asMs(accessors.activityAtOf(entry))
    : (entry) => readTime(entry, "latest_flight_at", "latestFlightAt", "updated_at", "updatedAt");
  const text = tailOf;
  if (normalized === "tail") {
    return list.sort((left, right) => text(left)
      .localeCompare(text(right), undefined, { sensitivity: "base", numeric: true }));
  }
  // Undated aircraft sort LAST in both date modes rather than jumping to an end
  // of the list on a missing timestamp, so a row with no date never displaces a
  // real one. Ties fall back to tail number to keep the order stable between
  // renders instead of depending on the incoming array order.
  const byDate = (getMs, newestFirst) => list.sort((left, right) => {
    const a = getMs(left); const b = getMs(right);
    if (a === null && b === null) {
      return text(left).localeCompare(text(right), undefined, { sensitivity: "base", numeric: true });
    }
    if (a === null) return 1;
    if (b === null) return -1;
    return newestFirst ? b - a : a - b;
  });
  if (normalized === "flights") {
    // Busiest first. Ties fall back to tail number so aircraft with the same
    // count (very common at 0) keep a stable, readable order.
    const countOf = (entry) => {
      const value = accessors.flightCountOf ? accessors.flightCountOf(entry) : entry?.flight_count;
      return Number.isFinite(Number(value)) ? Number(value) : 0;
    };
    return list.sort((left, right) => countOf(right) - countOf(left)
      || text(left).localeCompare(text(right), undefined, { sensitivity: "base", numeric: true }));
  }
  if (normalized === "created") {
    // Oldest first: the order you added them, which reads like a hand-built list.
    return byDate(createdOf, false);
  }
  // "recent": most recent activity first.
  return byDate(activityOf, true);
}

/**
 * Bind the three selects. `onRepaint` is called after an ordering change so the
 * fleet sidebar and flight lists re-sort without a reload; the theme control
 * does not need it because CSS reacts to `data-theme` on its own.
 */
export function setupDisplayPreferenceControls({ onRepaint = () => {} } = {}) {
  const doc = globalThis.document;
  if (typeof doc?.querySelector !== "function") return;
  for (const control of CONTROLS) {
    const select = doc.querySelector(`#${control.id}`);
    if (!select) continue;
    // Reflect the stored value, normalising first so a stale entry cannot leave
    // the select showing a value the app will not honour.
    select.value = control.normalize(control.load());
    if (select.dataset?.displayPreferenceBound === "1") continue;
    if (select.dataset) select.dataset.displayPreferenceBound = "1";
    select.addEventListener?.("change", () => {
      const value = control.normalize(select.value);
      control.save(value);
      // Re-read rather than trusting select.value: normalise may have rejected
      // it, and the control should show what actually took effect.
      select.value = value;
      control.onChange?.(value);
      // Ordering changes have to redraw immediately — the fleet rail and the
      // flights table are already on screen behind the Settings panel, and a
      // preference that only takes effect on the next reload reads as broken.
      if (control.repaint !== false) onRepaint(control.id, value);
    });
  }
}

/**
 * Apply the flight-order preference to an already newest-first list. Callers
 * that slice to "recent N" must call this BEFORE slicing, or "oldest first"
 * would reverse the newest N instead of showing the oldest.
 */
export function orderFlightsForDisplay(flightsNewestFirst = [], order = loadFlightOrder()) {
  return normalizeFlightOrder(order) === "oldest"
    ? [...flightsNewestFirst].reverse()
    : flightsNewestFirst;
}
